/** Page Section Header's CMS normalisers. No "use client", no React, no DOM
 *  and no import from a `.tsx`: both the server organism and its client
 *  sub-parts call these, and anything else here would drag a boundary across
 *  with them.
 *
 *  ⚠ EVERY SIGNATURE TAKES `unknown`, NOT THE UNION IT RETURNS. The live
 *  values these meet are "Center", "canter", "flex-start", "6.0n" and "0";
 *  typed to the union, none of these functions would be callable on CMS data. */

import type {
  PageSectionHeaderBackground,
  PageSectionHeaderColumnSpan,
  PageSectionHeaderCtaAlign,
  PageSectionHeaderLegalPosition,
  PageSectionHeaderTextAlign,
  PageSectionHeaderLayoutVariant
} from "./page-section-header.types";

/** True when the set lists the value as its OWN key. `hasOwnProperty` rather
 *  than a bracket lookup or `in`, which walk the prototype and would wave
 *  `constructor` and `toString` through. */
function listed(set: Record<string, true>, value: string): boolean {
  return Object.prototype.hasOwnProperty.call(set, value);
}

/** A text field's content, or "" for anything that is not text with ink in it.
 *  Shared because all three sub-parts and the organism normalise every CMS
 *  string through it, and four copies of a trim is four places for one of them
 *  to stop agreeing about what an empty field is. */
export function text(value: unknown): string {
  return typeof value === "string" ? value.trim() : "";
}

export const TEXT_ALIGNS: Record<PageSectionHeaderTextAlign, true> = {
  left: true,
  center: true,
  right: true
};

/** Closes D1 and D3, which cannot be split. D1: `getAlignment()` falls through
 *  every case, so it returns `center` whatever it is asked. D3: that helper and
 *  the eight inline ternaries beside it all compare case-sensitively, which
 *  leaves 149 live `Center` instances centred by accident. Replacing the
 *  comparisons without folding case moves all 149. Live spread: center 244,
 *  Center 149, empty 60, left 47, flex-start 1, canter 1.
 *
 *  `center` for anything off the list, which is where the 60 empty and the two
 *  off-contract instances land. */
export function resolveTextAlign(value: unknown): PageSectionHeaderTextAlign {
  const asked = typeof value === "string" ? value.trim().toLowerCase() : "";
  return listed(TEXT_ALIGNS, asked) ? (asked as PageSectionHeaderTextAlign) : "center";
}

export const CTA_ALIGNS: Record<PageSectionHeaderCtaAlign, true> = {
  left: true,
  center: true,
  right: true,
  beside_copy: true
};

/** `cta_align`'s reading, where the fallback is ANOTHER FIELD rather than a
 *  fixed value: unset, blank or off the contract, the buttons follow the copy.
 *  ⚠ The off-contract case is why this is a function and not the caller's
 *  `cta_align || text_align` — that convention sends `"flex-start"` to
 *  `resolveTextAlign`'s own `center`, centring buttons under left-aligned copy.
 *  The fallback is RESOLVED, not returned raw, so `text_align: "Center"` still
 *  arrives as `center`.
 *
 *  ⚠ ITS OWN WHITELIST, NOT `TEXT_ALIGNS`. `beside_copy` is a placement and not
 *  an alignment, so a function returning it under a `TextAlign` name would be a
 *  type whose name lies about what it carries; `text_align` keeps `TEXT_ALIGNS`
 *  and `resolveTextAlign`. Nothing else moves — the three alignment values and
 *  the fall-through to `text_align` resolve exactly as they did. */
export function resolveCtaAlign(value: unknown, fallback: unknown): PageSectionHeaderCtaAlign {
  const asked = typeof value === "string" ? value.trim().toLowerCase() : "";
  return listed(CTA_ALIGNS, asked) ? (asked as PageSectionHeaderCtaAlign) : resolveTextAlign(fallback);
}

export const COLUMN_SPANS: Record<PageSectionHeaderColumnSpan, true> = {
  6: true,
  8: true,
  12: true
};

/** Closes D11. Live spread: 6 x255, empty x120, 12 x67, 8 x52, and eight off
 *  the contract — "0" x2, "9" x2, "10" x2, "14", "6.0n". 497 of them arrive as
 *  strings and 28 as numbers, so both are accepted.
 *
 *  8 keeps the 120 empty instances where production's `Number(column || 8)`
 *  puts them. ⚠ It does NOT put "0" there: a non-empty string is truthy, so
 *  `||` never fires and those two span 0 today.
 *
 *  `hasOwnProperty` against `COLUMN_SPANS` rather than a tripled `===`, same
 *  reason as `listed()` above: the input is unsanitised CMS data, and a
 *  bracket lookup or `in` would wave `constructor`/`toString` through as a
 *  "column_span". A numeric key still works here — `hasOwnProperty.call`
 *  coerces it to a string the same way property access always has, so
 *  `asked === NaN` is rejected (`"NaN"` is not a listed key) exactly as the
 *  old `===` chain rejected it. */
export function resolveColumnSpan(value: unknown): PageSectionHeaderColumnSpan {
  const numeric = typeof value === "number" || typeof value === "string";
  const asked = numeric ? Number(value) : Number.NaN;
  return Object.prototype.hasOwnProperty.call(COLUMN_SPANS, asked) ? (asked as PageSectionHeaderColumnSpan) : 8;
}

export const LEGAL_POSITIONS: Record<PageSectionHeaderLegalPosition, true> = {
  below_cta: true,
  below_img: true,
  bottom: true
};

/** Closes D2. Production tests `below_cta` and `below_img` only, so the 11
 *  live `bottom` instances draw nothing. Legal draws in 0 of 502 either way,
 *  so the default is a forward choice with no migration behind it:
 *  `below_cta` is the branch production reaches for first. */
export function resolveLegalPosition(value: unknown): PageSectionHeaderLegalPosition {
  const asked = typeof value === "string" ? value.trim().toLowerCase() : "";
  const fallback: PageSectionHeaderLegalPosition = "below_cta";
  return listed(LEGAL_POSITIONS, asked) ? (asked as PageSectionHeaderLegalPosition) : fallback;
}

export const LAYOUT_VARIANTS: Record<PageSectionHeaderLayoutVariant, true> = {
  "IMG-AS-BG": true,
  "recog-PSH": true,
  "grid-based-image-PSH": true
};

/** `undefined` means the default band, which is where production already sends
 *  an unrecognised value — 4 live instances reach it that way
 *  (`PAGESECTIONHEADER` x3, `subhead1medium` x1). Case-sensitive on purpose:
 *  these are production's own spellings and no live value differs from one by
 *  case alone, so folding would only widen what the CMS can mean. */
export function resolveLayoutVariant(value: unknown): PageSectionHeaderLayoutVariant | undefined {
  const asked = typeof value === "string" ? value.trim() : "";
  return listed(LAYOUT_VARIANTS, asked) ? (asked as PageSectionHeaderLayoutVariant) : undefined;
}

/** Closes the band's paint to the published options. `transparent` is the
 *  default because 497 of 502 live instances leave the field empty, which is
 *  production's `backgroundColor: inherit`.
 *
 *  Lives here rather than in the organism: the story derives its
 *  `background_variant` options from this same set, and a story importing a
 *  const out of a Server Component `.tsx` is a worse coupling than one more
 *  Record in the module every sub-part already imports from.
 *  `resolveBackground` itself stays in the organism — it is the one caller. */
export const BACKGROUNDS: Record<PageSectionHeaderBackground, true> = {
  transparent: true,
  "neutral-100": true,
  "slate-950": true
};

/** Where the report says this block's links sit — both buttons and the badge's
 *  disclaimer link. ⚠ NOT `LINK_POSITION_BODY`, and not a tidier spelling: this
 *  is the placement the reports ALREADY HOLD. Production hands
 *  `"page_section_header"` to `getAnalyticsAttrs` (dtv-page-section-header.tsx:679),
 *  which writes it into `data-linkposition` lower-cased (core-events.js:166), and
 *  that attribute is what GTM's own Link Click trigger reads on all 104 live CTAs.
 *  The event this block deletes is the DUPLICATE PUSH, which carried `body`; the
 *  event that SURVIVES is GTM's, so it keeps GTM's value. Writing `body` here
 *  would retire the duplicate and move the surviving one, booking a second
 *  placement beside the figure the reports have been accumulating — the argument
 *  services/analytics makes for the nav constants (`:49-53`), applied here.
 *  Already lower case, so the literal written is the literal that lands. */
export const LINK_POSITION = "page_section_header";

/** The scheme that makes a CTA a call rather than a navigation. */
const PHONE_SCHEME = "tel:";

/** The ending that makes a CTA a download rather than a navigation. */
const PDF_EXTENSION = ".pdf";

/* ⚠ CASE-SENSITIVE, AND TESTED AGAINST THE WHOLE ADDRESS — production's own
   tests. `TEL:`, `.PDF` and `/terms.pdf?v=2` are all unreported; folding case or
   stripping a query here would start reporting clicks that have never been
   reported, which moves the totals a comparison is drawn against.
   ⚠ ONE DEFINITION FOR BOTH SIDES OF THE CLIENT BOUNDARY. The organism decides
   WHETHER a button gets the reporting seam and the seam decides WHICH report to
   make, so a literal kept twice is a destination one side reports and the other
   misses. This module is the one both can import: it holds no React and no
   "use client", so neither drags a boundary across. */

/** True when the destination dials rather than navigates. */
export function isPhoneCta(url: string): boolean {
  return url.startsWith(PHONE_SCHEME);
}

/** True when the destination downloads a document rather than navigating. */
export function isPdfCta(url: string): boolean {
  return url.endsWith(PDF_EXTENSION);
}

/** True when a click on this destination has something of its own to report.
 *  Everything else is reported by the container alone. */
export function reportsClick(url: string): boolean {
  return isPhoneCta(url) || isPdfCta(url);
}

/** The origin `isSafeImageSrc` parses against, so the gate and `sized` below
 *  read a scheme the same way. */
const CDN_BASE = "https://www.directv.com";

/** An address plus the CDN's resize/retina query — EXCEPT on a `data:` URI,
 *  which has no query to take: everything after the comma IS the payload, so a
 *  `?imwidth=600` lands inside the base64 and the picture decodes at no band at
 *  all. The gate admits `data:image/*` and the component's own console
 *  messages tell authors to use one, so the case is reachable.
 *
 *  Shared rather than kept in `section-header-media.tsx` alone: the organism's
 *  own `IMG-AS-BG` backdrop crops append the same suffixes to the same three
 *  fields and could not reach a copy defined only in that file.
 *
 *  ⚠ OFF THE PARSED SCHEME, not `startsWith`: the URL parser strips tab,
 *  newline and return before reading it, so a raw string can hide one inside
 *  `da<TAB>ta:` and still be the data URI the gate admitted — `isSafeImageSrc`
 *  reads it the same way and says why. Every caller has passed that gate, which
 *  parses the address itself, so this cannot throw.
 *
 *  ⚠ STILL A `?` ON AN ADDRESS THAT ALREADY CARRIES ONE. That second `?` makes
 *  `noretina` inert and production carries the same bug; flipping it to `&`
 *  would change which bytes every live instance is served — see
 *  design-conflicts/page-section-header.mdx. A different input from this one,
 *  and deliberately left alone. */
export function sized(url: string, query: string): string {
  return new URL(url, CDN_BASE).protocol === "data:" ? url : `${url}?${query}`;
}
